import { Sentence } from 'interfaces/db/sentence'
import { Transcript } from 'interfaces/db/transcript'
import { Word } from 'interfaces/db/word'

export interface OriginalSentenceMetadata {
  start: number
  end: number
  text: string
  confidence?: number
  speaker?: string
  words: Word[]
}

export interface CondensedSentence extends Sentence {
  originalSentences: OriginalSentenceMetadata[]
}

export interface CondensedTranscript extends Transcript {
  sentences: CondensedSentence[]
}

export function condenseTranscript(
  transcript: Transcript
): CondensedTranscript {
  if (!transcript?.sentences) {
    return { ...transcript, sentences: [] }
  }

  const maxSentences = 5
  const condensedSentences: CondensedSentence[] = []
  let currentSpeaker: string | null = null
  let currentParagraph = ''
  let currentStart: number | null = null
  let currentEnd: number | null = null
  let sentenceCount = 0
  let originalSentences: OriginalSentenceMetadata[] = []

  transcript.sentences.forEach((sentence) => {
    if (
      currentSpeaker === null ||
      currentSpeaker !== sentence.speaker ||
      sentenceCount >= maxSentences
    ) {
      if (currentParagraph) {
        condensedSentences.push({
          speaker: currentSpeaker || '',
          text: currentParagraph,
          start: currentStart!,
          end: currentEnd!,
          confidence: sentence.confidence,
          words: sentence.words,
          originalSentences: originalSentences,
        })
      }

      currentSpeaker = sentence.speaker
      currentParagraph = sentence.text
      currentStart = sentence.start
      currentEnd = sentence.end
      sentenceCount = 1
      originalSentences = [
        {
          start: sentence.start,
          end: sentence.end,
          text: sentence.text,
          speaker: sentence.speaker,
          words: sentence.words,
          confidence: sentence.confidence,
        },
      ]
    } else {
      currentParagraph += ' ' + sentence.text
      currentEnd = sentence.end
      sentenceCount++
      originalSentences.push({
        start: sentence.start,
        end: sentence.end,
        text: sentence.text,
        speaker: sentence.speaker,
        words: sentence.words,
        confidence: sentence.confidence,
      })
    }
  })

  if (currentParagraph) {
    condensedSentences.push({
      speaker: currentSpeaker || '',
      text: currentParagraph,
      start: currentStart!,
      end: currentEnd!,
      words: transcript.sentences[transcript.sentences.length - 1].words,
      originalSentences: originalSentences,
      confidence:
        transcript.sentences[transcript.sentences.length - 1].confidence,
    })
  }

  return { ...transcript, sentences: condensedSentences }
}

export function expandTranscript(condensed: CondensedTranscript): Transcript {
  const expandedSentences: Sentence[] = condensed.sentences.flatMap(
    (condensedSentence) =>
      condensedSentence.originalSentences.map((original) => ({
        confidence: original.confidence || 0, // Defaulting to 0 if confidence is not available
        end: original.end,
        speaker: original.speaker || '', // Defaulting to empty string if speaker is not available
        start: original.start,
        text: original.text,
        words: original.words,
      }))
  )

  return {
    ...condensed,
    sentences: expandedSentences,
  }
}
